// ----- Phase 5.X-submission-reorder — Reorder modal ----- //
//
// The modal that opens when the operator clicks the ↕ icon on an NDA
// group row in HomePage. Lists every unlocked sequence in the NDA,
// lets the operator drag rows up/down using native HTML5 drag-drop
// (no library — same pattern as XmlTab/ZipTab elsewhere in V2), runs
// `evaluateLifecycle` on every change to show conflicts in the
// embedded `LifecycleReportPanel`, and POSTs `apply` on confirm.
//
// Locked sequences appear in a separate "Locked" group below the
// draggable list, presented read-only so the operator sees the
// canonical numbering but can't move them.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactElement,
} from 'react';
import type { Submission } from '@/api/v2Client';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import { isSequenceLocked } from '@/constants/status';
import { labelForSequence } from '@/lib/sequenceLabel';
import {
  reorderApi,
  type AffectedDocument,
} from '@/lib/api/reorder';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { LifecycleReportPanel } from './LifecycleReportPanel';
import { ReorderRowDragHandle } from './ReorderRowDragHandle';

// ----- Public props -----------------------------------------------------------

export interface ReorderModalProps {
  /** The NDA the operator is reordering (display label + body param). */
  readonly nda: string;
  /** Drug name to show in the modal title for context. */
  readonly drugName: string;
  /** All sequences in the NDA, in their CURRENT order. */
  readonly sequences: readonly Submission[];
  /** Called after a successful apply so the dashboard can refetch. */
  readonly onSuccess: () => void;
  /** Called to close the modal without applying. */
  readonly onClose: () => void;
  /** Optional inspector deep-link for affected documents. */
  readonly onOpenDocument?: (documentId: string) => void;
}

// ----- Helpers ----------------------------------------------------------------

// phase-dashboard-lock-consistency — this used to be a second hand-rolled
// mirror of the backend ("Status IN ('Complete','Published') OR IsLocked"),
// which had drifted: it omitted Submitted, Approved, Rejected and
// PendingAgencyConfirmation. It only appeared correct on Velbaxin 0002
// (Status 'Submitted') because that row also carries IsLocked=true — a
// status-only Submitted sequence would have been wrongly draggable here.
// Now delegated to the ONE shared predicate, which mirrors the server's
// isSubmissionLocked() exactly and is what the dashboard row uses too.
const isLocked = isSequenceLocked;

function parseSeqInt(s: string | null | undefined): number {
  const n = Number.parseInt(String(s ?? '0'), 10);
  return Number.isFinite(n) ? n : 0;
}

// ISS035 — the original submission in an application is ALWAYS sequence 0000
// (CLAUDE.md "Original vs Supplement"). A row is the original when it has a real
// sequence number that parses to 0 (an absent SequenceNumber is NOT an
// original — parseSeqInt would read null as 0, so the null is excluded here).
function isOriginalSequence(s: Submission): boolean {
  return s.SequenceNumber != null && parseSeqInt(s.SequenceNumber) === 0;
}

interface SeqNumbered {
  readonly id: string;
  readonly n: number;
}

// ISS035 — THE one numbering function the modal uses for BOTH the lifecycle
// pre-flight and the apply payload, so the preview can never disagree with what
// apply actually sends (the old pre-flight used an arbitrary +1000 offset).
//
// Rules: the anchored original keeps 0000; locked rows keep their own numbers;
// the draggable supplements are a PERMUTATION of their own existing sequence
// numbers — reordering decides which supplement occupies which existing slot,
// it never invents a new number. The backend reorder guard
// (ReorderSequenceIdentityError) requires the post-reorder multiset of sequence
// numbers to equal the pre-reorder multiset exactly; allocating fresh lowest-
// free numbers would change that multiset and be refused. So the supplements'
// own current numbers are collected, sorted ascending, and handed out in that
// order to the supplements in their new display order.
function computeFullNumbering(
  supplementOrder: readonly Submission[],
  anchoredOriginal: Submission | null,
  lockedRows: readonly Submission[],
): SeqNumbered[] {
  const result: SeqNumbered[] = [];
  if (anchoredOriginal) {
    result.push({ id: anchoredOriginal.Id, n: 0 });
  }
  for (const s of lockedRows) {
    result.push({ id: s.Id, n: parseSeqInt(s.SequenceNumber) });
  }
  // The supplements' existing sequence numbers are the fixed slots; the reorder
  // permutes which supplement sits in which slot. Sorted ascending so the row
  // dragged to the top of the list takes the lowest existing supplement slot.
  const slots = supplementOrder
    .map((s) => parseSeqInt(s.SequenceNumber))
    .sort((a, b) => a - b);
  supplementOrder.forEach((row, i) => {
    result.push({ id: row.Id, n: slots[i] ?? parseSeqInt(row.SequenceNumber) });
  });
  return result;
}

// ----- Modal ------------------------------------------------------------------

export function ReorderModal({
  nda,
  drugName,
  sequences,
  onSuccess,
  onClose,
  onOpenDocument,
}: ReorderModalProps): ReactElement {
  // Partition once on mount/prop change. Locked rows are immutable and shown
  // beneath the draggable list. ISS035 — an UNLOCKED original 0000 is pulled
  // out as an anchored, read-only row: it must keep sequence 0000 and cannot be
  // dragged or renumbered. A LOCKED original is simply one of the locked rows
  // (already immutable, already at 0000), so it needs no special handling here.
  const { anchoredOriginal, initialSupplements, lockedRows } = useMemo(() => {
    const sorted = [...sequences].sort((a, b) =>
      (a.SequenceNumber ?? '').localeCompare(b.SequenceNumber ?? ''),
    );
    const supplements: Submission[] = [];
    const locked: Submission[] = [];
    let original: Submission | null = null;
    for (const s of sorted) {
      if (isLocked(s)) {
        locked.push(s);
      } else if (original === null && isOriginalSequence(s)) {
        original = s;
      } else {
        supplements.push(s);
      }
    }
    return { anchoredOriginal: original, initialSupplements: supplements, lockedRows: locked };
  }, [sequences]);

  // Working copy of the draggable supplement order. Drag operations mutate this;
  // the anchored original is never part of it.
  const [order, setOrder] = useState<Submission[]>(initialSupplements);

  // Lifecycle-impact panel state.
  const [evaluating, setEvaluating] = useState(false);
  const [affected, setAffected] = useState<readonly AffectedDocument[]>([]);
  const [unaffectedCount, setUnaffectedCount] = useState(0);
  const [evalError, setEvalError] = useState<string | null>(null);

  // Apply state.
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Re-evaluate any time the unlocked order changes.
  const evalSeq = useRef(0);
  const evaluateNow = useCallback(
    async (current: Submission[]) => {
      // ISS035 — evaluate against the ACTUAL post-reorder numbering (the same
      // function apply uses), not an approximation. The old code offset the
      // unlocked rows by +1000 and then re-normalised to 1..N, which could
      // reorder rows relative to the locked ones and disagree with what apply
      // would really write. The anchored original stays 0000; locked rows keep
      // their numbers; supplements permute their own existing slots.
      const payload = computeFullNumbering(current, anchoredOriginal, lockedRows).map(
        (row) => ({ id: row.id, sequenceNumber: row.n }),
      );

      const my = ++evalSeq.current;
      setEvaluating(true);
      setEvalError(null);
      try {
        const result = await reorderApi.evaluateLifecycle({
          nda,
          sequenceOrder: payload,
        });
        if (my !== evalSeq.current) return; // stale response, ignore
        setAffected(result.affectedDocuments);
        setUnaffectedCount(result.unaffectedDocumentCount);
      } catch (err) {
        if (my !== evalSeq.current) return;
        const body = parseApiErrorBody(err);
        setEvalError(body?.message ?? (err instanceof Error ? err.message : 'Evaluation failed'));
        setAffected([]);
        setUnaffectedCount(0);
      } finally {
        if (my === evalSeq.current) setEvaluating(false);
      }
    },
    [nda, anchoredOriginal, lockedRows],
  );

  useEffect(() => {
    void evaluateNow(order);
  }, [order, evaluateNow]);

  // ----- Drag-drop -----------------------------------------------------------

  // Track which row is being dragged. Using ref keeps `dragstart →
  // dragover → drop` synchronous without re-rendering on every move.
  const draggingIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const onDragStart = (idx: number) => (e: DragEvent<globalThis.HTMLLIElement>): void => {
    draggingIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    // Setting some data is required for Firefox to fire dragend.
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (idx: number) => (e: DragEvent<globalThis.HTMLLIElement>): void => {
    if (draggingIndex.current === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const onDragLeave = (): void => {
    setDragOverIndex(null);
  };

  const onDrop = (idx: number) => (e: DragEvent<globalThis.HTMLLIElement>): void => {
    e.preventDefault();
    const from = draggingIndex.current;
    draggingIndex.current = null;
    setDragOverIndex(null);
    if (from === null || from === idx) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (!moved) return prev;
      next.splice(idx, 0, moved);
      return next;
    });
  };

  const onDragEnd = (): void => {
    draggingIndex.current = null;
    setDragOverIndex(null);
  };

  // Keyboard accessibility — ↑/↓ on a focused row swaps with neighbor.
  // V1 had a "Reset" button for restoring the original order, port too.
  const moveRow = (idx: number, delta: -1 | 1): void => {
    setOrder((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      const a = next[idx];
      const b = next[target];
      if (!a || !b) return prev;
      next[idx] = b;
      next[target] = a;
      return next;
    });
  };

  const resetOrder = (): void => {
    setOrder(initialSupplements);
  };

  // ----- Apply ---------------------------------------------------------------

  const dirty = useMemo(() => {
    if (order.length !== initialSupplements.length) return true;
    return order.some((s, i) => s.Id !== initialSupplements[i]?.Id);
  }, [order, initialSupplements]);

  // phase-reorder-lifecycle-guard — the pre-flight is now honest: a detected
  // conflict BLOCKS Apply on the client (the button must not invite an action
  // the server will reject). Applying during a brief re-evaluation is still
  // safe — the server re-runs the SAME check freshly at apply time and refuses
  // a conflict authoritatively — so the button is not held merely for
  // `evaluating`; it is held only when a conflict is actually present.
  const applyBlocked = affected.length > 0;
  const canApply = dirty && !applying && !applyBlocked;

  const handleApply = async (): Promise<void> => {
    setApplying(true);
    setApplyError(null);
    try {
      // ISS035 — send the anchored original (pinned at 0000) and the
      // supplements under the SAME numbering the pre-flight evaluated. Locked
      // rows are NOT sent (the server keeps them, and including a locked row
      // would trip the LockedSequence guard). The server independently enforces
      // the original-0000 invariant as the authoritative gate.
      const lockedIds = new Set(lockedRows.map((s) => s.Id));
      const payload = computeFullNumbering(order, anchoredOriginal, lockedRows)
        .filter((e) => !lockedIds.has(e.id))
        .map((e) => ({ id: e.id, newSequenceNumber: e.n }));
      await reorderApi.apply({ nda, sequenceOrder: payload });
      onSuccess();
    } catch (err) {
      const body = parseApiErrorBody(err);
      let msg = body?.message ?? (err instanceof Error ? err.message : 'Reorder failed');
      // Surface specific backend errors with friendly messages.
      if (err instanceof ApiError && body?.error === 'LockedSequence') {
        msg = 'One or more sequences are now locked. Refresh the dashboard and try again.';
      } else if (err instanceof ApiError && body?.error === 'NdaMismatch') {
        msg = 'One or more sequences moved to a different NDA. Refresh and try again.';
      } else if (
        err instanceof ApiError &&
        (body?.error === 'LifecycleConflict' ||
          body?.error === 'CompiledSequence' ||
          body?.error === 'OriginalSequence')
      ) {
        // phase-reorder-lifecycle-guard — the server refused with its readable,
        // reference-naming message. Surface it verbatim; it already explains
        // exactly which references (or compiled sequences) block the reorder
        // and that nothing was changed. This is the freshness backstop: even if
        // the pre-flight looked clean, a conflict introduced since is caught
        // here and shown, not silently applied.
        msg = body?.message ?? msg;
      }
      setApplyError(msg);
    } finally {
      setApplying(false);
    }
  };

  // ----- Render --------------------------------------------------------------

  // phase-movable-dialogs — the shared movable shell; drag aside to see the
  // dashboard rows being reordered.
  return (
    <MovableDialog
      persistKey="dashboard.reorder"
      title={<span id="reorder-modal-title">Reorder sequences — {drugName}</span>}
      ariaLabelledBy="reorder-modal-title"
      testId="reorder-modal"
      onClose={onClose}
      closeOnBackdrop
      width={620}
      maxHeight="90vh"
      zIndex={1000}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            lineHeight: 1,
            color: 'var(--text-muted)',
          }}
        >
          ×
        </button>
      }
    >
      <>
        {/* phase-dashboard-lock-consistency — this read "(Complete /
            Published)". "Published" is an internal/legacy status name that is
            NOT in STATUS_CONFIG and never appears on the dashboard, while the
            statuses a user actually sees on a locked row — Submitted, Approved,
            Rejected — went unmentioned. Sharon's own case was a Submitted
            sequence sitting in the Locked group under a sentence that did not
            name it. The list below is the locked set expressed in the words the
            dashboard shows. */}
        <p style={{ marginTop: 0, color: 'var(--text-muted)', fontSize: 13 }}>
          NDA {nda} • Drag rows to set the new sequence order. Locked sequences
          (Complete, Submitted, Approved or Rejected) and original sequence 0000 cannot be reordered. Existing sequence numbers are retained.
        </p>

        {/* ISS035 — the original submission is pinned to sequence 0000 and
            shown read-only above the draggable list. The original must always
            keep 0000 (supplements continue from the highest existing sequence);
            reordering can never move it or hand 0000 to a supplement. A locked
            original instead appears in the Locked group below. */}
        {anchoredOriginal && (
          <div style={{ marginBottom: 10 }}>
            <ul
              data-testid="reorder-anchored-list"
              style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 6 }}
            >
              <li
                data-testid={`reorder-anchored-row-${anchoredOriginal.Id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                }}
              >
                <ReorderRowDragHandle disabled />
                <span style={{ fontFamily: 'monospace', fontSize: 13, minWidth: 50 }}>
                  {anchoredOriginal.SequenceNumber}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  Sequence {anchoredOriginal.SequenceNumber}
                  <span style={{ marginLeft: 8, fontSize: 12 }}>
                    (original — pinned to 0000)
                  </span>
                  <div
                    data-testid={`reorder-anchored-desc-${anchoredOriginal.Id}`}
                    style={{ fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={labelForSequence(anchoredOriginal)}
                  >
                    {labelForSequence(anchoredOriginal)}
                  </div>
                </span>
              </li>
            </ul>
          </div>
        )}

        {/* Draggable list */}
        <ol
          aria-label="Draggable sequence order"
          data-testid="reorder-list"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          {order.map((row, idx) => (
            <li
              key={row.Id}
              draggable
              tabIndex={0}
              data-testid={`reorder-row-${row.Id}`}
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDragLeave={onDragLeave}
              onDrop={onDrop(idx)}
              onDragEnd={onDragEnd}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  moveRow(idx, 1);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  moveRow(idx, -1);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderBottom:
                  idx < order.length - 1 ? '1px solid var(--border)' : 'none',
                background:
                  dragOverIndex === idx ? 'var(--surface)' : 'transparent',
                cursor: 'grab',
              }}
            >
              <ReorderRowDragHandle />
              <span
                style={{ fontFamily: 'monospace', fontSize: 13, minWidth: 50 }}
                data-testid={`reorder-row-${row.Id}-position`}
              >
                #{idx + 1}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                Sequence {row.SequenceNumber}
                <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                  ({row['Status'] ?? 'Draft'})
                </span>
                {/* phase-dashboard-coherence item 4 — the sequence's identity,
                    from the SAME evaluator the dashboard sub-rows render
                    (labelForSequence): no reordering blind numbers. */}
                <div
                  data-testid={`reorder-desc-${row.Id}`}
                  style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={labelForSequence(row)}
                >
                  {labelForSequence(row)}
                </div>
              </span>
            </li>
          ))}
          {order.length === 0 && (
            <li style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>
              No unlocked sequences to reorder.
            </li>
          )}
        </ol>

        {lockedRows.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: 13, color: 'var(--text-muted)' }}>
              Fixed sequences (read-only)
            </h4>
            <ul
              data-testid="reorder-locked-list"
              style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 6 }}
            >
              {lockedRows.map((row, i) => (
                <li
                  key={row.Id}
                  data-testid={`reorder-locked-row-${row.Id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderBottom: i < lockedRows.length - 1 ? '1px solid var(--border)' : 'none',
                    background: 'var(--surface)',
                    color: 'var(--text-muted)',
                  }}
                >
                  <ReorderRowDragHandle disabled />
                  <span style={{ fontFamily: 'monospace', fontSize: 13, minWidth: 50 }}>
                    {row.SequenceNumber}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    Sequence {row.SequenceNumber}
                    <span style={{ marginLeft: 8, fontSize: 12 }}>
                      ({String(row['Status'] ?? 'Locked')})
                    </span>
                    {/* item 4 — locked rows keep their locked presentation but
                        are named just the same. */}
                    <div
                      data-testid={`reorder-locked-desc-${row.Id}`}
                      style={{ fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={labelForSequence(row)}
                    >
                      {labelForSequence(row)}
                    </div>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Lifecycle report */}
        <div style={{ marginTop: 14 }}>
          {evalError !== null ? (
            <div
              role="alert"
              data-testid="lifecycle-report-error"
              style={{
                padding: '10px 12px',
                borderRadius: 6,
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#7f1d1d',
                fontSize: 13,
              }}
            >
              Could not evaluate lifecycle impact: {evalError}
            </div>
          ) : (
            <LifecycleReportPanel
              affectedDocuments={affected}
              unaffectedDocumentCount={unaffectedCount}
              evaluating={evaluating}
              {...(onOpenDocument !== undefined && { onOpenDocument })}
            />
          )}
        </div>

        {applyError !== null && (
          <div
            role="alert"
            data-testid="apply-error"
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 6,
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              color: '#7f1d1d',
              fontSize: 13,
            }}
          >
            {applyError}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            onClick={resetOrder}
            disabled={!dirty || applying}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'transparent',
              cursor: dirty && !applying ? 'pointer' : 'not-allowed',
              color: dirty && !applying ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'transparent',
              cursor: applying ? 'not-allowed' : 'pointer',
              fontSize: 13,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={!canApply}
            data-testid="reorder-apply-button"
            title={
              applyBlocked
                ? 'Apply is blocked while a lifecycle conflict exists. Resolve the affected references first.'
                : undefined
            }
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid var(--primary)',
              background: canApply ? 'var(--primary)' : 'var(--surface)',
              color: canApply ? '#fff' : 'var(--text-muted)',
              cursor: canApply ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {applying
              ? 'Applying…'
              : applyBlocked
                ? 'Apply blocked — resolve conflicts'
                : 'Apply reorder'}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
